/**
 * The ready button (2026-10-28): during a wait (the countdown, a breather, a story wipe, the Book's last words, the
 * results once their panel is closed) a button near the top says what pressing it does, "Next wave now (N)". It
 * sends `next`; once everyone in the room has pressed it the wait ends, so alone it is one press. With friends it
 * counts who is ready. N (the keymap's ready key) does the same from the keyboard.
 */

import type { WaitKind } from '@shared/index.js';
import type { ClientState } from '../state/clientState.js';
import { type Screen, button, clear, setText } from './dom.js';

const WAIT_LABEL: Readonly<Record<WaitKind, string>> = {
  countdown: 'Start now',
  breather: 'Next wave now',
  results: 'Back to the lobby now',
  wipe: 'Stand up now',
  end: 'Skip',
};

/** What the button says, and whether it can still be pressed. `total`: connected players; `ready`: how many pressed. */
export function waitButtonText(wait: WaitKind, ready: number, total: number, mine: boolean, key: string): { label: string; enabled: boolean } {
  const what = key ? `${WAIT_LABEL[wait]} (${key})` : WAIT_LABEL[wait];
  if (total <= 1) return { label: what, enabled: true };
  if (mine) return { label: `Ready. Waiting for the others (${ready}/${total})`, enabled: false };
  return { label: `${what}: ${ready}/${total} ready`, enabled: true };
}

export interface WaitButtonDeps {
  send(): void;
  /** The results panel is up (its own button does this job). */
  resultsShown(): boolean;
  /** The ready key's label, '' when unbound. */
  keyLabel(): string;
}

export function mountWaitButton(root: HTMLElement, state: ClientState, deps: WaitButtonDeps): Screen {
  clear(root);
  root.classList.add('wait-bar');
  const btn = button('', () => deps.send(), 'btn btn-primary wait-btn');
  root.appendChild(btn);
  root.hidden = true;

  const unsubscribe = state.subscribe(() => {
    const room = state.room;
    const wait = room?.wait;
    if (!room || !wait || (wait === 'results' && deps.resultsShown())) {
      root.hidden = true;
      return;
    }
    const here = room.players.filter((p) => p.connected);
    const me = here.find((p) => p.id === state.pid);
    const t = waitButtonText(wait, here.filter((p) => p.ready).length, here.length, me?.ready === true, deps.keyLabel());
    setText(btn, t.label);
    btn.disabled = !t.enabled;
    root.hidden = false;
  });

  return {
    show(): void { /* driven by the room frame */ },
    hide(): void { root.hidden = true; },
    dispose(): void {
      unsubscribe();
      clear(root);
    },
  };
}
